import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from 'react-redux';
import request from '../../helpers/request';
import { uploadFile } from '../../helpers/storage';
import { toast } from 'react-toastify';

// MUI Components
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/joy/Typography';
import { Button, CircularProgress } from "@mui/joy";
import { ArrowForward } from "@mui/icons-material";

// Local components
import TaskResultHeader from './TaskResultHeader';
import DocumentPreview from './DocumentPreview';
import EventLogsCard from './EventLogsCard';
import ExtractedFieldsCard from './ExtractedFieldsCard';
import ValidationsCard from './ValidationsCard';
import ReferencesCard from './ReferencesCard';
import { DeleteTaskModal, TestResultModal, FeedbackModal } from './modals';
import { updateNestedObject } from './utils';

const TaskResult = () => {
  const [currentPage, setCurrentPage] = useState({});
  const { pages } = useSelector(state => state.policies);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    if (pages.length > 0 && Object.keys(currentPage).length === 0) {
      setCurrentPage(pages[0]);
    }
  }, [pages, currentPage]);

  const { id } = useParams();
  const [isLoading, setLoading] = useState(true);

  const navigate = useNavigate();
  const goBackHandle = () => navigate("/app/tasks");

  const [result, setResult] = useState({});
  const [isTestResultOpen, setTestResultOpen] = useState(false);
  const [testResult] = useState(null);
  const [isFeedbackModalOpen, setFeedbackModalOpen] = useState(false);

  // Derived state
  const getOutput = result?.output?.metadata ? result?.output?.metadata : result?.output;
  const getFields = getOutput?.documentFields;
  const getValidations = getOutput?.validation;
  const getValidationStats = (key) => getOutput?.extractionStats?.validation[key];

  const [input, setInput] = useState(null);
  const payload = input?.payload;

  const getDocument = input?.document;

  // Derive document type from input
  const getDocumentType = useMemo(() => {
    const legacyType = input?.payload?.document?.type;
    if (legacyType) {
      if (legacyType === "application/pdf") return "pdf";
      const img = legacyType.match(/^image\/([a-zA-Z0-9]+)/);
      if (img) return img[1].toLowerCase();
    }
    if (typeof getDocument === "string") {
      if (getDocument.startsWith("data:")) {
        const m = getDocument.match(/^data:([^;]+);/);
        if (m) {
          if (m[1] === "application/pdf") return "pdf";
          const imgMatch = m[1].match(/^image\/([a-zA-Z0-9]+)/);
          if (imgMatch) return imgMatch[1].toLowerCase();
        }
      }
      const match = getDocument.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
      return match ? match[1].toLowerCase() : undefined;
    }
    return undefined;
  }, [input?.payload?.document?.type, getDocument]);

  const getReferences = payload?.objectReference;

  const isEvaluation = input?.type?.toLowerCase() === 'evaluation';

  const [isEditing, setIsEditing] = useState(false);
  const [documentPage, setDocumentPage] = useState(1);
  const [invertColors, setInvertColors] = useState(false);

  // Data loading functions
  const loadInputAndResults = useCallback(async () => {
    setLoading(true);
    try {
      const resultsUrl = `/api/v1/tools/result/${id}/`;
      const inputsUrl = `/api/v1/tools/task/${id}/`;

      const [resultData, inputData] = await Promise.allSettled([
        request.get(resultsUrl),
        request.get(inputsUrl),
      ]);

      if (resultData.status === 'fulfilled') {
        setResult(resultData.value.data);
      } else {
        console.error("Failed to fetch result:", resultData.reason);
      }

      if (inputData.status === 'fulfilled') {
        setInput(inputData.value.data);
      } else {
        console.error("Failed to fetch input:", inputData.reason);
      }

      const taskExists = inputData.status === 'fulfilled' && inputData.value.data;

      if (!taskExists) {
        console.warn(`Task ${id} does not exist or has invalid status:`, inputData.value?.data?.status);
        toast.error(`Task ${id} is not accessible. Task not found`);
        navigate('/app/tasks');
        return;
      }
    } catch (e) {
      console.error("Unexpected error in loadInputAndResults", e);
      toast.error("Failed to load task data");
      navigate('/app/tasks');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadInputData = useCallback(async () => {
    try {
      const inputsUrl = `/api/v1/tools/task/${id}/`;
      const response = await request.get(inputsUrl);
      const newInputData = response.data;

      if (input?.status === 'in progress' && (newInputData.status === 'completed' || newInputData.status === 'action required')) {
        const resultsUrl = `/api/v1/tools/result/${id}/`;
        const resultResponse = await request.get(resultsUrl);
        setResult(resultResponse.data);
      }

      const updatedInputData = {
        ...newInputData,
        payload: {
          ...newInputData.payload,
          document: input?.payload?.document ? {
            ...newInputData.payload?.document,
            base64: input.payload.document.base64,
            type: input.payload.document.type
          } : newInputData.payload?.document
        }
      };

      setInput(updatedInputData);
    } catch (e) {
      console.error("Failed to fetch input data:", e);
    }
  }, [id, input?.status, input?.payload?.document]);

  // Action handlers
  const handleReplayClick = async () => {
    if (!input) {
      console.warn("No input data available to rerun the task.");
      return alert("No input data available to rerun the task.");
    }

    try {
      await request.post(`/api/v2/tools/task/${id}/rerun/`);
      toast.success("Task is being re-run successfully!");
      navigate('/app/tasks');
    } catch (error) {
      toast.error("Failed to re-run task. Please try again.");
    }
  };

  const handleDeleteTask = async () => {
    try {
      await request.delete(`/api/v1/tools/task/${id}/`);
      toast.success("Task archived successfully!");
      navigate('/app/tasks');
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task. Please try again.");
    }
    setDeleteModalOpen(false);
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setResult((prev) => {
      const path = prev?.output?.metadata ? ["output", "metadata", "documentFields"] : ["output", "documentFields"];
      return updateNestedObject(prev, path.concat(name), value);
    });
  };

  const handleSave = async () => {
    try {
      const updatedData = { output: result.output };
      await request.patch(`/api/v1/tools/result/${result.id}/`, updatedData);
      setIsEditing(false);
      toast.success('Extracted fields updated successfully!');
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error('Failed to save task result. Please try again.');
    }
  };

  const continueValidation = async () => {
    try {
      await request.post(`api/v1/tools/result/create_validations/`, { validated_extraction: result.output, task_result_id: result.id });
      navigate(`/app/tasks/result/${id}`);
      toast.success("Successfully ran human validation.");
    } catch (error) {
      console.error('Error:', error);
      toast.error('Unable to run manual validation.');
    }
  };

  const redirectToPolicy = async () => {
    try {
      if (!input.payload.policy_id) {
        console.error("No policy_id found in input payload");
        return;
      }

      const policyUrl = `/api/v1/compliance/policy/${input.payload.policy_id}/`;
      const response = await request.get(policyUrl);

      const policy = response.data;
      const path = policy?.id ? `/app/policies/wizard/${policy.id}` : '/app/policies/wizard';
      navigate(path, { state: { policy } });
    } catch (error) {
      console.error("Error fetching compliance policy:", error);
    }
  };

  const handleGoToAgentChat = () => {
    const policyId = input?.payload?.policy_id;
    const params = new URLSearchParams();
    params.set('taskId', id);
    if (policyId) params.set('policyId', policyId);
    navigate(`/app/agents?${params.toString()}`);
  };

  const validateTaskBeforeNavigation = async (taskId) => {
    try {
      const response = await request.get(`/api/v1/tools/task/${taskId}/`);
      const task = response.data;
      const validStatuses = ['in progress', 'completed', 'action required', 'DISQUALIFIED'];

      if (!task || !validStatuses.includes(task.status)) {
        toast.error(`Task ${taskId} is not accessible. ${!task ? 'Task not found.' : 'Invalid task status.'}`);
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error validating task:", error);
      toast.error(`Task ${taskId} is not accessible.`);
      return false;
    }
  };

  const handleExportJson = async () => {
    try {
      const response = await request.post(
        "api/v1/tools/export_task_result/",
        { task_id: result.task, format: "json" },
        { responseType: "json" }
      );

      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `task_${result.task}_result.json`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Exported as JSON!");
    } catch (error) {
      console.error("JSON export failed", error);
      toast.error("Failed to export JSON");
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await request.post(
        "api/v1/tools/export_task_result/",
        { task_id: result.task, format: "xlsx" },
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `task_${result.task}_result.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Exported as Excel!");
    } catch (error) {
      console.error("Excel export failed", error);
      toast.error("Failed to export Excel");
    }
  };

  const handleLike = async () => {
    try {
      const updatedOutput = {
        ...result.output,
        feedback: { type: "like" },
      };
      await request.patch(`/api/v1/tools/result/${result.id}/`, { output: updatedOutput });
      setResult((prev) => ({ ...prev, output: updatedOutput }));
      toast.success("Thank you for your feedback!");
    } catch (error) {
      console.error("Failed to save feedback:", error);
      toast.error("Failed to save feedback");
    }
  };

  const handleAddToEvaluation = async () => {
    try {
      const policyId = input?.payload?.policy_id;
      if (!policyId) {
        toast.error("No policy ID found for this task.");
        return;
      }

      // 1. Get Compliance Policy Name
      const policyRes = await request.get(`/api/v1/compliance/policy/${policyId}/`);
      const compliancePolicy = policyRes.data;
      const policyName = compliancePolicy.name;

      // 2. Find Evaluation Policy
      // Search by name instead of "Evaluation"
      const searchRes = await request.get(`/api/v1/compliance/policy/?search=${encodeURIComponent(policyName)}`);
      const allPolicies = searchRes.data.results || searchRes.data;

      let evalPolicy = allPolicies.find(p =>
        p.settings?.type?.toLowerCase() === 'evaluation' &&
        p.name === policyName &&
        String(p.settings?.linked_policy_id) === String(policyId)
      );

      if (!evalPolicy) {
        // Create new evaluation policy
        const newPolicyPayload = {
          name: policyName,
          settings: {
            type: "Evaluation",
            linked_policy_id: policyId,
            tool: compliancePolicy.settings?.tool
          },
          policy: {
            documents: []
          }
        };
        const createRes = await request.post('/api/v1/compliance/policy/', newPolicyPayload);
        evalPolicy = createRes.data;
        toast.success(`Created new evaluation policy: ${evalPolicy.name}`);
      }

      // 3. Prepare Document
      const docId = `doc-${Date.now()}`;
      // Infer extension from document type or default to pdf
      const extension = getDocumentType === 'pdf' ? 'pdf' : getDocumentType || 'pdf';
      const docName = `Task-${id}.${extension}`;

      let s3Key = null;
      // Document can be at input.document (string URL) or input.payload.document (object with base64)
      const docUrl = getDocument || input?.payload?.document?.base64 || input?.payload?.document;

      if (!docUrl) {
        toast.error("No document found for this task.");
        return;
      }

      // Try to upload to S3
      try {
        let blob;
        if (docUrl.startsWith('data:')) {
          const res = await fetch(docUrl);
          blob = await res.blob();
        } else {
          // If it's a URL, try to fetch it to get the blob
          // Note: This might fail with CORS if the URL is external and doesn't allow it
          const res = await fetch(docUrl);
          blob = await res.blob();
        }

        const file = new File([blob], docName, { type: blob.type });
        s3Key = `evaluations/${evalPolicy.id}/${docId}/${docName}`;

        toast.info("Uploading document to evaluation dataset...");
        await uploadFile(file, s3Key);
      } catch (e) {
        console.error("Failed to upload document copy:", e);
        // If upload fails but we have a URL (not data URI), we can try to use it
        if (docUrl.startsWith('data:')) {
          toast.error("Could not upload document and it is a data URL. Cannot add to evaluation.");
          return;
        }
        // If it's a remote URL, we might proceed without uploading, but evaluation policy usually expects s3Key/path
        // We'll warn the user
        toast.warn("Could not upload a copy of the document. Linking original URL.");
      }

      // 4. Add to Dataset
      const newDoc = {
        id: docId,
        name: docName,
        url: s3Key ? null : docUrl, // If we have s3Key, url will be generated on read
        path: s3Key,
        s3Key: s3Key,
        documentFields: result.output.documentFields || {},
        validations: []
      };

      const currentDocuments = evalPolicy.policy.documents || [];
      const updatedDocuments = [...currentDocuments, newDoc];

      const patchPayload = {
        policy: {
          ...evalPolicy.policy,
          documents: updatedDocuments
        }
      };

      await request.patch(`/api/v1/compliance/policy/${evalPolicy.id}/`, patchPayload);
      toast.success("Successfully added task result to evaluation dataset!");

    } catch (error) {
      console.error("Error adding to evaluation:", error);
      toast.error("Failed to add to evaluation policy.");
    }
  };

  const handleDislike = async ({ field, comment }) => {
    try {
      const updatedOutput = {
        ...result.output,
        feedback: { type: "dislike", field, comment },
      };
      await request.patch(`/api/v1/tools/result/${result.id}/`, { output: updatedOutput });
      setResult((prev) => ({ ...prev, output: updatedOutput }));
      toast.success("Thank you for your feedback!");
    } catch (error) {
      console.error("Failed to save feedback:", error);
      toast.error("Failed to save feedback");
    }
  };

  // Effects
  useEffect(() => {
    loadInputAndResults();
  }, [loadInputAndResults]);

  useEffect(() => {
    let intervalId;

    if (input?.status === 'in progress') {
      intervalId = setInterval(() => {
        loadInputData();
      }, 3000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [input?.status, loadInputData]);

  // Memoized document URL
  const documentUrl = useMemo(() => {
    if (!getDocument || !getDocumentType) return "";

    if (getDocumentType === "pdf") {
      return `${getDocument}#page=${documentPage}&toolbar=0`;
    } else if (["png", "jpeg", "jpg"].includes(getDocumentType)) {
      return getDocument;
    }
    return "";
  }, [getDocument, getDocumentType, documentPage]);

  // Loading state
  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100vh">
        <CircularProgress />
        <Typography>Loading...</Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', width: { xs: '100vw', lg: '100%' } }}>
      <TaskResultHeader
        id={id}
        inputStatus={input?.status}
        onDelete={() => setDeleteModalOpen(true)}
        onReplay={handleReplayClick}
        onContinue={continueValidation}
        onGoToPolicy={redirectToPolicy}
        onGoToAgentChat={handleGoToAgentChat}
        onBack={goBackHandle}
        onExportJson={handleExportJson}
        onExportExcel={handleExportExcel}
        onLike={handleLike}
        onAddToEvaluation={handleAddToEvaluation}
        onDislike={() => setFeedbackModalOpen(true)}
        currentFeedback={getOutput?.feedback}
        isEvaluation={isEvaluation}
      />

      <Box
        sx={{
          flex: 1,
          width: '100%',
          overflowY: { xs: 'auto', lg: 'hidden' },
          maxHeight: { xs: '100vh', lg: 'none' },
        }}
      >
        <Grid container spacing={2} sx={{ height: '100%' }}>
          {/* Document Preview */}
          <Grid size={{ lg: 6, md: 12 }} sx={{ height: '100%', overflow: "auto", width: "100%", order: { xs: 2, lg: 1 } }}>
            <DocumentPreview
              id={id}
              documentUrl={documentUrl}
              documentType={getDocumentType}
              invertColors={invertColors}
              onToggleInvert={() => setInvertColors(!invertColors)}
              hasDocument={!!getDocument}
            />
          </Grid>

          {/* Extracted Fields & Other Cards */}
          <Grid size={{ lg: 6, md: 12 }} sx={{ height: '100%', overflow: "auto", width: "100%", order: { xs: 1, lg: 2 } }}>
            <Box sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <Stack spacing={2} sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
                {/* Event Logs (inline when in progress or failed) */}
                {['in progress', 'failed', 'error'].includes(input?.status) && (
                  <EventLogsCard events={input?.events} />
                )}

                {/* References, Token Usage, Policy Output, Origin Task */}
                {!isEvaluation && (
                  <ReferencesCard
                    id={id}
                    tokenUsage={getOutput?.token_usage}
                    references={getReferences}
                    originalTaskId={payload?.original_task_id}
                    policyOutputId={input?.payload?.policy_output_id}
                  />
                )}

                {/* Extracted Fields */}
                <ExtractedFieldsCard
                  getFields={getFields}
                  getOutput={getOutput}
                  result={result}
                  isEditing={isEditing}
                  onEditToggle={() => setIsEditing(true)}
                  onSave={handleSave}
                  onFieldChange={handleFieldChange}
                  setDocumentPage={setDocumentPage}
                  groundTruth={getReferences}
                  isEvaluation={isEvaluation}
                />

                {/* Validations */}
                <ValidationsCard
                  id={id}
                  validations={getValidations}
                  getValidationStats={getValidationStats}
                />
              </Stack>

              {/* Next Pending Task */}
              <Stack direction="row" sx={{ w: "full", justifyContent: "end", pt: 1 }}>
                {input?.next_pending_task && (
                  <Button
                    startDecorator={<ArrowForward />}
                    onClick={async () => {
                      const isValid = await validateTaskBeforeNavigation(input.next_pending_task);
                      if (isValid) {
                        navigate(`/app/tasks/result/${input.next_pending_task}`);
                      }
                    }}
                    sx={{ w: "50%" }}
                  >
                    Next Pending Task
                  </Button>
                )}
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Modals */}
      <DeleteTaskModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        taskId={id}
        onConfirm={handleDeleteTask}
      />

      <TestResultModal
        open={isTestResultOpen}
        onClose={() => setTestResultOpen(false)}
        testResult={testResult}
      />

      <FeedbackModal
        open={isFeedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        fieldNames={getFields ? Object.keys(getFields) : []}
        onSubmit={handleDislike}
      />
    </Box>
  );
};

export default TaskResult;
